import { Alert, Steps, Table, Tag, Collapse, Descriptions, Typography, Divider, Space } from 'antd';
import {
  ScanOutlined,
  FileAddOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  TrophyOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const statusData = [
  { status: 'DRAFT', color: 'default', meaning: 'Awaiting staff review', actions: 'Approve, Reject, Edit Address' },
  { status: 'APPROVED', color: 'processing', meaning: 'Card order queued', actions: 'View only' },
  { status: 'PROCESSING', color: 'processing', meaning: 'PayCenter processing', actions: 'View only' },
  { status: 'COMPLETED', color: 'success', meaning: 'Card ordered successfully', actions: 'View only' },
  { status: 'REJECTED', color: 'error', meaning: 'Staff rejected', actions: 'View only' },
  { status: 'CANCELLED', color: 'default', meaning: 'Cancelled', actions: 'View only' },
];

const statusColumns = [
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: string, record: { color: string }) => <Tag color={record.color}>{status}</Tag>,
  },
  { title: 'Meaning', dataIndex: 'meaning', key: 'meaning' },
  { title: 'Staff Actions', dataIndex: 'actions', key: 'actions' },
];

const workflowSteps = [
  {
    title: 'Detection',
    description: 'Weekly job (Monday 3 AM UTC) scans for cards expiring within 60 days.',
    icon: <ScanOutlined />,
  },
  {
    title: 'Draft Created',
    description: 'System creates CardRenewalDraft, sends push notification "Card Expiring Soon" to user.',
    icon: <FileAddOutlined />,
  },
  {
    title: 'Staff Review',
    description: 'Staff reviews draft details. Can edit delivery address if needed before approval.',
    icon: <EyeOutlined />,
  },
  {
    title: 'Approval/Rejection',
    description: 'Staff approves (queues card order at PayCenter) or rejects with reason.',
    icon: <CheckCircleOutlined />,
  },
  {
    title: 'Card Ordering',
    description: 'PayCenter processes the physical card order asynchronously.',
    icon: <SyncOutlined />,
  },
  {
    title: 'Completion',
    description: 'Card ordered, user notified "New Card Ordered", draft marked COMPLETED.',
    icon: <TrophyOutlined />,
  },
];

const faqItems = [
  {
    key: '1',
    label: 'When are cards checked for expiration?',
    children: (
      <Paragraph>
        The system runs a scheduled job every <strong>Monday at 3:00 AM UTC</strong>. Admins can also trigger a manual
        check anytime using the "Trigger Check" button in the header.
      </Paragraph>
    ),
  },
  {
    key: '2',
    label: 'Why was a draft not created for an expiring card?',
    children: (
      <Paragraph>
        Drafts are only created for users with complete profile data. Check that the user has:
        <ul>
          <li>First name and last name</li>
          <li>Complete address (street, postal code, city)</li>
          <li>Active PayCenter account</li>
        </ul>
        Also, if a draft already exists for the same user and expiration date, duplicates are prevented.
      </Paragraph>
    ),
  },
  {
    key: '3',
    label: 'Can I edit a draft after approval?',
    children: (
      <Paragraph>
        <strong>No.</strong> Drafts can only be edited while in DRAFT status. Once approved, the card order is
        immediately queued at PayCenter and cannot be modified. If changes are needed, the draft must be cancelled and a
        new one created.
      </Paragraph>
    ),
  },
  {
    key: '4',
    label: 'What happens if the PayCenter order fails?',
    children: (
      <Paragraph>
        The draft will remain in PROCESSING status. Contact technical support to investigate the PayCenter job failure.
        The system may need manual intervention to retry the order or cancel the draft.
      </Paragraph>
    ),
  },
  {
    key: '5',
    label: 'How do I manually trigger an expiration check?',
    children: (
      <Paragraph>
        Click the <strong>"Trigger Check"</strong> button in the page header (Admin only). You can customize:
        <ul>
          <li>
            <strong>Days before expiration:</strong> How many days ahead to look (default: 60)
          </li>
          <li>
            <strong>Batch size:</strong> Maximum users to process per run (default: 100)
          </li>
        </ul>
      </Paragraph>
    ),
  },
  {
    key: '6',
    label: 'What notifications do users receive?',
    children: (
      <Paragraph>
        Users receive two push notifications during the renewal process:
        <ol>
          <li>
            <strong>"Card Expiring Soon"</strong> - When the draft is created
          </li>
          <li>
            <strong>"New Card Ordered"</strong> - When the card order completes successfully
          </li>
        </ol>
      </Paragraph>
    ),
  },
];

export function CardRenewalDocumentationTab() {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Overview */}
      <Alert
        type="info"
        showIcon
        message="Card Renewal Auto-Renewal System"
        description="The system automatically detects cards expiring within 60 days and creates renewal drafts for staff review. Cards are renewed through PayCenter after staff approval."
      />

      {/* Workflow */}
      <div>
        <Title level={4}>Renewal Workflow</Title>
        <Steps
          direction="vertical"
          size="small"
          current={-1}
          items={workflowSteps.map(step => ({
            title: step.title,
            description: step.description,
            icon: step.icon,
            status: 'finish',
          }))}
        />
      </div>

      <Divider />

      {/* Status Reference */}
      <div>
        <Title level={4}>Status Reference</Title>
        <Table
          dataSource={statusData}
          columns={statusColumns}
          rowKey="status"
          pagination={false}
          size="small"
          style={{ marginBottom: 16 }}
        />
        <Text type="secondary">
          <strong>State Flow:</strong> DRAFT → APPROVED → PROCESSING → COMPLETED (or REJECTED/CANCELLED from DRAFT)
        </Text>
      </div>

      <Divider />

      {/* Configuration */}
      <div>
        <Title level={4}>System Configuration</Title>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Schedule">Every Monday at 3:00 AM UTC</Descriptions.Item>
          <Descriptions.Item label="Detection Window">60 days before expiration</Descriptions.Item>
          <Descriptions.Item label="Batch Size">100 users per run</Descriptions.Item>
          <Descriptions.Item label="Duplicate Prevention">One active draft per user/expiration</Descriptions.Item>
        </Descriptions>
      </div>

      <Divider />

      {/* Staff Actions */}
      <div>
        <Title level={4}>Staff Actions</Title>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="View Queue">
            Filter by status, date range, or search by ID/user. Click any row to view details.
          </Descriptions.Item>
          <Descriptions.Item label="Bulk Actions">
            Select multiple DRAFT items using checkboxes, then approve or reject all at once.
          </Descriptions.Item>
          <Descriptions.Item label="Edit Address">
            Change delivery address (only while in DRAFT status). Click row → Edit in detail modal.
          </Descriptions.Item>
          <Descriptions.Item label="Trigger Check">
            Admin-only. Manually trigger expiration scan with custom parameters.
          </Descriptions.Item>
        </Descriptions>
      </div>

      <Divider />

      {/* FAQ */}
      <div>
        <Title level={4}>
          <QuestionCircleOutlined style={{ marginRight: 8 }} />
          Frequently Asked Questions
        </Title>
        <Collapse items={faqItems} />
      </div>
    </Space>
  );
}
